import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import ProgressPanel from './ProgressPanel';

const HighResPanel = ({
  settings,
  onChange,
  disabledReason,
  loading,
  error,
  stage,
  progress,
  recommendPanel,
}) => {
  const { t } = useTranslation(['analysis', 'common']);
  return (
  <Box>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
      {t('estimationPage.highResDescription')}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {t('estimationPage.highResNote')}
    </Typography>
    <Grid container spacing={2} sx={{ mt: 1 }} alignItems="center">
      <Grid item xs={12} sm={6} md={3}>
        <TextField
          fullWidth
          type="number"
          label={t('estimationPage.thresholdEventsLabel')}
          value={settings.minGroupEvents}
          onChange={onChange.minGroupEvents}
          helperText={t('estimationPage.thresholdEventsHelper')}
          inputProps={{ min: 0 }}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <TextField
          fullWidth
          type="number"
          label={t('estimationPage.thresholdActiveYearsLabel')}
          value={settings.minActiveYears}
          onChange={onChange.minActiveYears}
          helperText={t('estimationPage.thresholdActiveYearsHelper')}
          inputProps={{ min: 0 }}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <TextField
          fullWidth
          type="number"
          label={t('estimationPage.thresholdSpanYearsLabel')}
          value={settings.minSpanYears}
          onChange={onChange.minSpanYears}
          helperText={t('estimationPage.thresholdSpanYearsHelper')}
          inputProps={{ min: 0 }}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <FormControlLabel
          control={(
            <Checkbox
              checked={settings.poolLowQuality}
              onChange={onChange.poolLowQuality}
            />
          )}
          label={t('estimationPage.poolLowQualityLabel')}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
          {t('estimationPage.poolLowQualityHelper')}
        </Typography>
      </Grid>
    </Grid>

    <Grid container spacing={2} sx={{ mt: 1 }} alignItems="center">
      <Grid item xs={12} sm={6} md={3}>
        <FormControlLabel
          control={(
            <Checkbox
              checked={settings.solowGuardEnabled}
              onChange={onChange.solowGuardEnabled}
            />
          )}
          label={t('estimationPage.solowGuardLabel')}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
          {t('estimationPage.solowGuardHelper')}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <TextField
          fullWidth
          type="number"
          label={t('estimationPage.solowGuardMaxRatioLabel')}
          value={settings.solowMaxRatio}
          onChange={onChange.solowMaxRatio}
          helperText={t('estimationPage.solowGuardMaxRatioHelper')}
          inputProps={{ min: 0, step: 0.1 }}
          disabled={!settings.solowGuardEnabled}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <TextField
          fullWidth
          type="number"
          label={t('estimationPage.solowGuardMinR2Label')}
          value={settings.solowMinR2}
          onChange={onChange.solowMinR2}
          helperText={t('estimationPage.solowGuardMinR2Helper')}
          inputProps={{ min: 0, max: 1, step: 0.01 }}
          disabled={!settings.solowGuardEnabled}
        />
      </Grid>
    </Grid>

    <Grid container spacing={2} sx={{ mt: 1 }} alignItems="center">
      <Grid item xs={12} sm={6} md={3}>
        <FormControlLabel
          control={(
            <Checkbox
              checked={settings.curveGuardEnabled}
              onChange={onChange.curveGuardEnabled}
            />
          )}
          label={t('estimationPage.curveGuardLabel')}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
          {t('estimationPage.curveGuardHelper')}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <TextField
          fullWidth
          type="number"
          label={t('estimationPage.curveGuardMinR2Label')}
          value={settings.curveMinR2}
          onChange={onChange.curveMinR2}
          helperText={t('estimationPage.curveGuardMinR2Helper')}
          inputProps={{ min: 0, max: 1, step: 0.01 }}
          disabled={!settings.curveGuardEnabled}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <FormControlLabel
          control={(
            <Checkbox
              checked={settings.highResComputeCI}
              onChange={onChange.highResComputeCI}
            />
          )}
          label={t('estimationPage.highResComputeCILabel')}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
          {t('estimationPage.highResComputeCIHelper')}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <TextField
          fullWidth
          type="number"
          label={t('estimationPage.highResBootstrapSamplesLabel')}
          value={settings.highResBootstrapSamples}
          onChange={onChange.highResBootstrapSamples}
          inputProps={{ min: 10, max: 200 }}
          disabled={!settings.highResComputeCI}
        />
      </Grid>
    </Grid>

    {recommendPanel}

    {disabledReason && (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {disabledReason}
      </Typography>
    )}

    {loading && (
      <ProgressPanel
        size={28}
        title={t('estimationPage.highResInProgress')}
        stage={stage}
        progress={progress}
        containerSx={{ py: 3 }}
      />
    )}

    {error && !loading && (
      <Alert severity="warning" sx={{ mt: 2 }}>
        {error}
      </Alert>
    )}
  </Box>
  );
};

export default HighResPanel;
